import * as React from 'react';
import PropTypes from 'prop-types';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { styled } from '@mui/material/styles';

// Clean, Neutral TableCell
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 700,
  textTransform: 'uppercase',
  fontSize: '0.75rem',
  letterSpacing: '0.5px',
  padding: '16px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  whiteSpace: 'nowrap',
  transition: 'all 0.2s ease',
  '&:hover': {
    color: theme.palette.text.primary,
  },
}));

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

export const getComparator = (order, orderBy) => (order === 'desc'
  ? (a, b) => descendingComparator(a, b, orderBy)
  : (a, b) => -descendingComparator(a, b, orderBy));

const EnhancedTableHead = ({
  order, orderBy, headCells, onRequestSort, sx
}) => {
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };
  const getCellAlign = (headCell) => {
    if (['left', 'center', 'right'].includes(headCell.align)) {
      return headCell.align;
    }
    return headCell.numeric ? 'right' : 'left';
  };
  const getSortLabelSx = (cellAlign) => {
    const isCenterAligned = cellAlign === 'center';
    const isRightAligned = cellAlign === 'right';
    let justifyContent = 'flex-start';

    if (isCenterAligned) {
      justifyContent = 'center';
    } else if (isRightAligned) {
      justifyContent = 'flex-end';
    }

    return {
      width: '100%',
      display: 'inline-flex',
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent,
      ...(isCenterAligned
        ? {
          position: 'relative',
          paddingLeft: '18px',
          paddingRight: '18px'
        }
        : {}),
      '&.MuiTableSortLabel-root': {
        color: 'inherit',
      },
      '&.MuiTableSortLabel-root.Mui-active': {
        color: 'primary.main',
      },
      '& .MuiTableSortLabel-icon': {
        color: 'primary.main !important',
        margin: 0,
        ...(isCenterAligned ? { position: 'absolute', right: 4 } : {}),
      },
    };
  };

  return (
    <TableHead sx={sx}>
      <TableRow>
        {headCells.map((headCell) => {
          const cellAlign = getCellAlign(headCell);
          return (
            <StyledTableCell
              key={headCell.id}
              sortDirection={orderBy === headCell.id ? order : false}
              align={cellAlign}
              sx={headCell.sx}
            >
              {!headCell.disable ? (
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={createSortHandler(headCell.id)}
                  sx={getSortLabelSx(cellAlign)}
                >
                  {headCell.icon && (
                    <headCell.icon style={{ marginRight: 8, fontSize: 18, opacity: 0.7 }} />
                  )}
                  {headCell.name}
                </TableSortLabel>
              ) : (
                headCell.name
              )}
            </StyledTableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
};

EnhancedTableHead.propTypes = {
  headCells: PropTypes.array.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  sx: PropTypes.object, // Added Prop
};

export default EnhancedTableHead;
